import type { User } from 'firebase/auth';
import { shouldTreatAsOnboardingComplete } from './onboardingAccountState';

export type UserRole = 'creator' | 'brand';

/**
 * Post-auth routing (pure). Callers should run `getPostAuthRouteWithLazyMigration` (Firestore) first
 * so lazy creator role inference can run when appropriate.
 *
 * Decision order:
 * 1. Missing user doc → `/select-role`, except legacy-only auth bypass → `/profile/edit`.
 * 2. No usable role (creator / influencer / brand) → `/select-role`.
 * 3. Brand → `/brand/dashboard` if onboarding completed, else `/onboarding/brand`.
 * 4. Creator/influencer → `/profile/edit` only if onboarding is explicitly completed or legacy bypass
 *    applies; otherwise resume creator wizard from `onboarding.step`.
 */
export function getPostAuthRoute(
  userDoc: Record<string, unknown> | null | undefined,
  authUser?: User | null,
): string {
  if (!userDoc) {
    if (authUser && shouldTreatAsOnboardingComplete(authUser, null)) {
      return '/profile/edit';
    }
    return '/select-role';
  }

  const role = (userDoc.role as UserRole | undefined) ?? (userDoc.userType as string | undefined);

  if (!role || (role !== 'creator' && role !== 'brand' && role !== 'influencer')) {
    return '/select-role';
  }

  const effectiveCreator = role === 'creator' || role === 'influencer';

  if (effectiveCreator) {
    if (shouldTreatAsOnboardingComplete(authUser ?? null, userDoc)) {
      return '/profile/edit';
    }
    const step = userDoc.onboarding?.step;
    if (step === 1) return '/onboarding/step-2';
    if (step === 2) return '/onboarding/step-3';
    return '/onboarding/step-1';
  }

  if (role === 'brand') {
    const onboardingCompleted =
      userDoc.onboardingCompleted === true || userDoc.onboardingStatus === 'completed';
    return onboardingCompleted ? '/brand/dashboard' : '/onboarding/brand';
  }

  return '/select-role';
}
